/**
 * Sound Feedback — Web Audio API for scanner feedback sounds.
 * Uses same sounds as Flutter offline: Leitura_Normal.mp3 (success), Item_Nao_lido.mp3 (error)
 * Supports volume control via GainNode with localStorage persistence.
 */

let audioContext: AudioContext | null = null;
let masterGain: GainNode | null = null;
let successBuffer: AudioBuffer | null = null;
let errorBuffer: AudioBuffer | null = null;
let initialized = false;

const VOLUME_KEY = 'feedback_volume';
const DEFAULT_VOLUME = 0.5;

async function loadAudioBuffer(url: string): Promise<AudioBuffer | null> {
  try {
    if (!audioContext) return null;
    const response = await fetch(url);
    const arrayBuffer = await response.arrayBuffer();
    return await audioContext.decodeAudioData(arrayBuffer);
  } catch (e) {
    console.warn(`[SoundFeedback] Failed to load ${url}:`, e);
    return null;
  }
}

/**
 * Initialize audio context, gain node, and preload sounds.
 * Must be called after user interaction (browser policy).
 */
export async function initSoundFeedback(): Promise<void> {
  if (initialized) return;
  try {
    audioContext = new AudioContext();
    masterGain = audioContext.createGain();
    masterGain.connect(audioContext.destination);

    // Load saved volume
    const savedVol = localStorage.getItem(VOLUME_KEY);
    masterGain.gain.value = savedVol ? parseFloat(savedVol) : DEFAULT_VOLUME;

    console.log('[SoundFeedback] AudioContext created, state:', audioContext.state, 'volume:', masterGain.gain.value);
    const [s, e] = await Promise.all([
      loadAudioBuffer('/sounds/Leitura_Normal.mp3'),
      loadAudioBuffer('/sounds/Item_Nao_lido.mp3'),
    ]);
    successBuffer = s;
    errorBuffer = e;
    initialized = true;
    console.log('[SoundFeedback] Initialized OK — success:', !!s, 'error:', !!e);
  } catch (err) {
    console.warn('[SoundFeedback] Init failed:', err);
  }
}

function playBuffer(buffer: AudioBuffer | null): void {
  if (!buffer || !audioContext || !masterGain) {
    console.warn('[SoundFeedback] playBuffer: no buffer, context, or gain');
    return;
  }
  // Resume context if suspended (browser policy)
  if (audioContext.state === 'suspended') {
    audioContext.resume();
  }
  const source = audioContext.createBufferSource();
  source.buffer = buffer;
  source.connect(masterGain);
  source.start(0);
}

export function playSuccess(): void {
  console.log('[SoundFeedback] playSuccess');
  playBuffer(successBuffer);
}

export function playError(): void {
  console.log('[SoundFeedback] playError');
  playBuffer(errorBuffer);
}

/**
 * Play attention tone — oscillator beep for two-read waiting state.
 * 600Hz for 200ms.
 */
export function playAttention(): void {
  console.log('[SoundFeedback] playAttention');
  if (!audioContext || !masterGain) {
    console.warn('[SoundFeedback] playAttention: no audioContext');
    return;
  }
  if (audioContext.state === 'suspended') {
    audioContext.resume();
  }
  const oscillator = audioContext.createOscillator();
  const gainNode = audioContext.createGain();
  oscillator.connect(gainNode);
  gainNode.connect(masterGain);
  oscillator.type = 'sine';
  oscillator.frequency.setValueAtTime(600, audioContext.currentTime);
  gainNode.gain.setValueAtTime(0.3, audioContext.currentTime);
  gainNode.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + 0.2);
  oscillator.start(audioContext.currentTime);
  oscillator.stop(audioContext.currentTime + 0.2);
}

/**
 * Play test beep — confirms sound is working.
 * 440Hz for 150ms.
 */
export function playTestBeep(): void {
  console.log('[SoundFeedback] playTestBeep');
  if (!audioContext) {
    // Init inline if not ready
    audioContext = new AudioContext();
    masterGain = audioContext.createGain();
    masterGain.connect(audioContext.destination);
    const savedVol = localStorage.getItem(VOLUME_KEY);
    masterGain.gain.value = savedVol ? parseFloat(savedVol) : DEFAULT_VOLUME;
  }
  if (audioContext.state === 'suspended') {
    audioContext.resume();
  }
  const oscillator = audioContext.createOscillator();
  const gainNode = audioContext.createGain();
  oscillator.connect(gainNode);
  gainNode.connect(masterGain!);
  oscillator.type = 'sine';
  oscillator.frequency.setValueAtTime(440, audioContext.currentTime);
  gainNode.gain.setValueAtTime(0.4, audioContext.currentTime);
  gainNode.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + 0.15);
  oscillator.start(audioContext.currentTime);
  oscillator.stop(audioContext.currentTime + 0.15);
}

export function isInitialized(): boolean {
  return initialized;
}

/**
 * Set master volume (0.0 to 1.0) and persist to localStorage.
 */
export function setVolume(volume: number): void {
  const clamped = Math.max(0, Math.min(1, volume));
  if (masterGain) {
    masterGain.gain.value = clamped;
  }
  localStorage.setItem(VOLUME_KEY, clamped.toString());
  console.log('[SoundFeedback] Volume set to', clamped);
}

/**
 * Get current volume (0.0 to 1.0).
 */
export function getVolume(): number {
  if (masterGain) return masterGain.gain.value;
  const saved = localStorage.getItem(VOLUME_KEY);
  return saved ? parseFloat(saved) : DEFAULT_VOLUME;
}
